import * as React from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import { JsonView, defaultStyles } from 'react-json-view-lite';
import { formatCurrency, longDateFormatStr } from 'stores/JSHelper';
const deepcopy = require('deepcopy');



type ResponseTablePropTypes = {
  data: any;
  responseError: any;
  dateColumns?: { [key: string]: boolean };
  amountColumns?: { [key: string]: boolean };
  showTableName?: boolean;
  showResponseTable?: boolean;
}

type TableItem = {
  [key: string]: any
}

type TableDataType = {
  loading: boolean;
  tables: {
    [key: string]: { colums: any[]; data: any[] }
  };
}

const columnHelper = createColumnHelper<TableItem>();

const RenderTable = ({ tableName, columns, data, showTableName }) => {
  const table = useReactTable({
    data,
    columns,
    enableSorting: false,
    onSortingChange: () => false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  return (
    <div className="overflow-x-scroll">
      {
        showTableName ? (
          <div className="text-xl font-bold text-navy-700 dark:text-white mt-5">{tableName}</div>
        ) : null
      }
      <table className="w-full mb-10">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="!border-px !border-gray-400">
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={() => false}
                    className="cursor-pointer border-b-[1px] border-gray-200 px-3 pb-2 pr-4 pt-4 text-start"
                  >
                    <div className="items-center justify-between text-xs text-gray-200">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: '',
                        desc: '',
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {table.getRowModel().rows.length > 0 && (
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id} className="odd:bg-gray-50">
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id} className="min-w-[150px] border-white/0 py-3 pr-6">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        )}
        {table.getRowModel().rows.length === 0 && (
          <tbody>
            <tr>
              <td colSpan={100} className="py-5 text-center">
                No data found
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  )
}

const CopyJsonButton = ({ json }) => {
  const [copied, setCopied] = React.useState<boolean>(false);
  const onClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(json));
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }
  return (
    <button onClick={onClick} className='absolute top-5 right-5 flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-xs font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30' >
      {copied ? "Copied" : "Copy Json"}
    </button>
  )
}


const ResponseTable: React.FC<ResponseTablePropTypes> = ({ data, responseError, dateColumns, amountColumns, showTableName, showResponseTable }) => {
  const [activeTab, setActiveTab] = React.useState<string>('table');
  const [tableData, setTableData] = React.useState<TableDataType>({ loading: true, tables: {} }); // tables: {"name": { colums: [], data: [] }}
  console.log("tableData", tableData);
  const [tableParsingError, setTableParsingError] = React.useState(false);

  React.useEffect(() => {
    const flattenObj = (ob) => {
      if (typeof ob !== "object") return ob;
      let result = {};
      for (const i in ob) {
        if (Array.isArray(ob[i])) {
          const temp = [];
          for (let j = 0; j < ob[i].length; j += 1) {
            temp.push(flattenObj(ob[i][j]));
          }
          result[i] = temp;
        }
        else if ((typeof ob[i]) === 'object' && !Array.isArray(ob[i])) {
          const temp = flattenObj(ob[i]);
          for (const j in temp) {
            result[i + '-' + j] = temp[j];
          }
        }
        else {
          result[i] = ob[i];
        }
      }
      return result;
    };

    const prepareTable = (flatenData: any): TableDataType => {
      const preparedData: TableDataType = { loading: false, tables: {} };
      const uniqueColumns: { [key: string]: { [key: string]: boolean } } = {};

      const formatColumn = (id, value) => {
        if (dateColumns && dateColumns[id]) {
          return longDateFormatStr(value);
        }
        if (amountColumns && amountColumns[id]) {
          return formatCurrency(value, "INR");
        }
        return value;
      }

      const recursion = (ob: any, tableName: string, split: string = 'status', splitHandled: boolean = false) => {
        if (typeof ob !== "object") return recursion({ message: ob }, tableName, split, splitHandled);
        if (ob[split] && !splitHandled) return recursion(ob, `${tableName}-${ob[split]}`, split, true);
        if (!preparedData.tables[tableName]) preparedData.tables[tableName] = { colums: [], data: [] };
        if (!uniqueColumns[tableName]) uniqueColumns[tableName] = {};
        for (let i in ob) {
          if (Array.isArray(ob[i])) {
            for (let j = 0; j < ob[i].length; j += 1) {
              let newOb = ob[i][j];
              if (Array.isArray(newOb)) newOb = { data: newOb };
              recursion(newOb, i, split, false);
            }
          }
          else {
            if (!uniqueColumns[tableName][i]) {
              uniqueColumns[tableName][i] = true;
              preparedData.tables[tableName].colums.push(
                columnHelper.accessor(i, {
                  id: i,
                  header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">{i}</p>,
                  cell: (info: any) => {
                    return (
                      <div className="flex items-center">
                        <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">{formatColumn(info.column.id, info.getValue())}</p>
                      </div>
                    )
                  },
                }
                )
              );
            }
          }
        }
        preparedData.tables[tableName].data.push(ob);
      }
      if (flatenData) {
        if (Array.isArray(flatenData)) flatenData = { data: flatenData };
        recursion(flatenData, "response");
      }
      return preparedData;
    }

    try {
      setTableData({ loading: true, tables: {} });
      setTableParsingError(false);
      const flatenData: any = flattenObj(deepcopy(data));
      setTableData(prepareTable(flatenData));
    } catch (err: any) {
      setTableParsingError(true);
      setActiveTab("json");
    }

  }, [data, amountColumns, dateColumns]);

  if (responseError) {
    return (
      <JsonView data={responseError} shouldInitiallyExpand={(level) => true} style={defaultStyles} />
    )
  }
  if (!data || tableData.loading) return null;
  const inactiveLinkClassName = 'inline-block p-4 border-b-2 border-transparent rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300';
  const activeLinkClassName = 'inline-block p-4 border-b-2 border-blue-600 rounded-t-lg active text-blue-600 dark:text-blue-500 dark:border-blue-500';
  return (
    <>
      <div className="text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700">
        <ul className="flex flex-wrap -mb-px">
          {
            !tableParsingError ? (
              <li className="mr-2">
                <button
                  className={activeTab === "table" ? activeLinkClassName : inactiveLinkClassName}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTab('table');
                  }}
                >Table</button>
              </li>
            ) : null
          }
          <li className="mr-2">
            <button onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setActiveTab("json");
            }}
              className={activeTab === "json" ? activeLinkClassName : inactiveLinkClassName}
            >Json</button>
          </li>
        </ul>
      </div>
      {
        activeTab === "table" ? (
          <>
            {Object.keys(tableData.tables).map(table => {
              if(table === 'response' && !showResponseTable) return null;
              return (<RenderTable key={table} tableName={table} columns={tableData.tables[table].colums || []} data={tableData.tables[table].data || []} showTableName={showTableName} />)
            })}
          </>
        ) : null
      }
      {
        activeTab === "json" ? (
          <div className='relative' >
            <CopyJsonButton json={data} />
            <JsonView data={data} shouldInitiallyExpand={(level) => true} style={defaultStyles} />
          </div>
        ) : null
      }
    </>
  );
}

ResponseTable.defaultProps = {
  showTableName: true,
  showResponseTable: true,
}

export default React.memo(ResponseTable);